import type { InfoContent } from "@core/components/InfoDialog";

/**
 * The Council's description of the Volunteer Stream Monitoring program,
 * carried over from the ArcGIS dashboard this replaces.
 *
 * Three editorial changes to the original text:
 *
 *  - The opening navigation paragraph described the ArcGIS layout ("select a
 *    site in the lower right", "use the arrows at the bottom of the chart").
 *    Those controls do not exist here, so it is rewritten for this app.
 *  - The program description names conductivity and temperature alongside
 *    macroinvertebrates. Neither is recorded anywhere in the monitoring base
 *    this dashboard reads, so those two sections say so rather than implying
 *    a reader will find them in the charts.
 *  - The scoring sections state the actual cut-points. The original copy
 *    explains what the three counts mean but not how they become a score and
 *    a grade, which is the question a reader looking at a letter on the map
 *    is most likely to have. The values are the Council's own, read from the
 *    formulas in the monitoring base.
 */
export const ABOUT: InfoContent = {
  title: "About this dashboard",

  lede: [
    "Select a monitoring site on the map or in the site list to see its own record. " +
    "If a stream has more than one site, filter to the stream first with the Stream " +
    "control — which also gives you a permanent link to that stream — and every site on " +
    "it is drawn together for comparison. Use the ribbon above the chart to switch " +
    "between the score, its three components and the raw counts behind them.",

    "The Volunteer Stream Monitoring (VSM) program uses macroinvertebrates, " +
    "conductivity, and temperature to gauge stream health.",
  ],

  sections: [
    {
      heading: "Macroinvertebrates",
      body: [
        "Macroinvertebrates are organisms lacking a backbone that can be seen with the " +
        "naked eye. More specifically, the term “macroinvertebrate” refers to groups of " +
        "organisms such as clams, snails, worms, mussels, and insects in their nymph and " +
        "larval stages.",
      ],
    },
    {
      heading: "Conductivity",
      body: [
        "Conductivity is a measure of the ability of water to conduct an electric current, " +
        "which is dependent on the concentration of charged particles in the water. " +
        "Research shows that conductivity in surface waters is an indicator of human " +
        "disturbance in a watershed. Conductivity levels between 150 and 800 µS/cm are " +
        "considered healthy for surface waters in Northern Michigan.",
      ],
      criteria: [
        {
          label: "Healthy range, Northern Michigan surface waters",
          value: "150 – 800 µS/cm",
          applied: false,
        },
      ],
      note:
        "Conductivity is collected by the program but is not held in the monitoring " +
        "records behind this dashboard, so it cannot be charted here. The Comprehensive " +
        "Water Quality dashboard does show conductivity for the sites it covers.",
    },
    {
      heading: "Temperature",
      body: [
        "Temperature is important to monitor as it is crucial to the survival of " +
        "macroinvertebrates and the fisheries in Northern Michigan.",
      ],
      note:
        "As with conductivity, temperature is collected by the program but is not held in " +
        "the monitoring records behind this dashboard.",
    },
    {
      heading: "How a site is scored",
      body: [
        "During program analysis for VSM, Watershed Council staff look at three main " +
        "numbers in regard to the macroinvertebrate data collected at each site. These " +
        "three numbers are the total number of families found, the number of EPT families " +
        "found, and the number of sensitive families found at each site. These three " +
        "numbers from each site generate a stream site score, and furthermore a stream grade.",
      ],
      criteria: [
        { label: "Total families found", value: "up to 20 points" },
        { label: "EPT families found", value: "up to 30 points" },
        { label: "Sensitive families found", value: "up to 50 points" },
        { label: "Site score", value: "sum, out of 100" },
      ],
    },
    {
      heading: "Total families found",
      body: [
        "The total number of families found is important because diverse macroinvertebrate " +
        "populations are used as indicators of stream ecosystem health and overall water " +
        "quality. In general, the more families found at a site, the better the water " +
        "quality is at the site.",
      ],
      criteria: [
        { label: "Fewer than 5 families", value: "0 points" },
        { label: "5 to 9", value: "3 points" },
        { label: "10 to 14", value: "5 points" },
        { label: "15 to 16", value: "10 points" },
        { label: "17 to 19", value: "15 points" },
        { label: "20 or more", value: "20 points" },
      ],
    },
    {
      heading: "EPT families",
      body: [
        "The acronym EPT refers to the three orders: Ephemeroptera (mayflies), Plecoptera " +
        "(stoneflies), and Trichoptera (caddisflies). Many of the families within these " +
        "three orders of macroinvertebrates are sensitive to pollution. These " +
        "macroinvertebrates are also an important food source for many other aquatic species.",
      ],
      criteria: [
        { label: "None found", value: "0 points" },
        { label: "Fewer than 2", value: "5 points" },
        { label: "2 to 3", value: "10 points" },
        { label: "4 to 5", value: "15 points" },
        { label: "6 to 7", value: "20 points" },
        { label: "8 or more", value: "30 points" },
      ],
    },
    {
      heading: "Sensitive families",
      body: [
        "The number of sensitive families refers to the total number of families which are " +
        "sensitive to pollution. This includes many of the EPT families above, as well as " +
        "other families which are heavily impacted by pollution, and not captured in the " +
        "EPT number.",
      ],
      criteria: [
        { label: "None found", value: "0 points" },
        { label: "Fewer than 2", value: "10 points" },
        { label: "2", value: "20 points" },
        { label: "3", value: "30 points" },
        { label: "4", value: "40 points" },
        { label: "5 or more", value: "50 points" },
      ],
    },
    {
      heading: "Stream grade",
      body: [
        "The site score out of 100 becomes the annual grade shown on the map and beside " +
        "each site in the list. A stream's own grade is rolled up from its sites, so it " +
        "does not swing with whichever sites happened to be sampled in a given year.",
      ],
      criteria: [
        { label: "A", value: "70 and above" },
        { label: "B", value: "50 to 69" },
        { label: "C", value: "30 to 49" },
        { label: "D", value: "20 to 29" },
        { label: "E", value: "10 to 19" },
        { label: "F", value: "below 10" },
      ],
    },
  ],

  footer: [
    "Criteria shown in grey are part of the program but are not held in the monitoring " +
    "records behind this dashboard, so nothing here is charted or graded against them.",
    "Data is read live from the Council's monitoring records. Use Refresh to pull the " +
    "latest.",
    "This dashboard is published as part of Water Watch, a programme of The Downstream " +
    "Project: thedownstreamproject.org/services/water-watch",
  ],
};
